import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowLeft, Trash2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCart(savedCart);
  }, []);

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success('Removed from cart');
  };

  const updateQuantity = (index, quantity) => {
    const newCart = [...cart];
    newCart[index].quantity = Math.max(1, Math.min(quantity, newCart[index].stock));
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.current_price * item.quantity, 0);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const memberDiscount = user.membership_type === 'premium' ? subtotal * 0.1 : 0;
  const total = subtotal - memberDiscount;

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/90 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <Sparkles className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-semibold text-[#2C3E50]">LuxLyx</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-8 text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Continue Shopping
        </Button>

        <h1 className="text-4xl font-serif font-semibold mb-8 text-[#2C3E50]">Shopping Cart</h1>

        {cart.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingCart className="h-20 w-20 text-[#8B9AAC] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#5A6C7D] text-xl mb-6">Your cart is empty</p>
            <Button
              data-testid="shop-now-btn"
              onClick={() => navigate('/products')}
              className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, index) => (
                <div
                  key={index}
                  data-testid={`cart-item-${index}`}
                  className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm"
                >
                  <div className="flex gap-6">
                    <img
                      src={item.images[0] || 'https://via.placeholder.com/150'}
                      alt={item.name}
                      className="w-24 h-24 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-serif font-semibold text-lg mb-2 text-[#2C3E50]">{item.name}</h3>
                      <p className="text-[#5A6C7D] text-sm mb-3 line-clamp-2">{item.description}</p>
                      <div className="flex items-center space-x-4">
                        <div className="text-2xl font-serif font-semibold text-[#1A3A52]">${item.current_price}</div>
                        <div className="text-sm text-[#8B9AAC] line-through">${item.mrp}</div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <Button
                        data-testid={`remove-${index}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromCart(index)}
                        className="text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <div className="flex items-center space-x-2">
                        <Button
                          data-testid={`decrease-${index}`}
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          className="border-[#E5E1DC] text-[#5A6C7D] hover:bg-[#F5F3F0] w-8 h-8 p-0"
                        >
                          -
                        </Button>
                        <span className="w-8 text-center font-medium text-[#2C3E50]">{item.quantity}</span>
                        <Button
                          data-testid={`increase-${index}`}
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          className="border-[#E5E1DC] text-[#5A6C7D] hover:bg-[#F5F3F0] w-8 h-8 p-0"
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm sticky top-6">
                <h2 className="text-2xl font-serif font-semibold mb-6 text-[#2C3E50]">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-[#5A6C7D]">
                    <span>Subtotal</span>
                    <span className="font-medium text-[#2C3E50]">${subtotal.toFixed(2)}</span>
                  </div>
                  {memberDiscount > 0 && (
                    <div className="flex justify-between text-[#1A3A52]">
                      <span>Premium Discount (10%)</span>
                      <span className="font-medium">-${memberDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-[#E5E1DC] pt-4">
                    <div className="flex justify-between">
                      <span className="text-lg font-serif font-semibold text-[#2C3E50]">Total</span>
                      <span className="text-2xl font-serif font-semibold text-[#1A3A52]">${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="checkout-btn"
                  onClick={handleCheckout}
                  className="w-full bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium py-6 text-lg"
                >
                  Proceed to Checkout
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
